'use client';

// Client despite holding no state: the rows below hand Base UI's client `render`
// prop an element of their own, and a server element there has no type in the
// client bundle — the popup then throws React #130 on open and unmounts the
// header, in a production build only.

import {
  SearchComboboxGroup,
  SearchComboboxGroupLabel,
  SearchComboboxItem,
} from '@vs-shop/ui/searchCombobox';
import {
  SearchSuggestProductImage,
  SearchSuggestProductImagePlaceholder,
  SearchSuggestProductLabel,
  SearchSuggestProductLink,
  SearchSuggestProductPrice,
  SearchSuggestVariant,
} from '@vs-shop/commerce-ui/searchPanel';

import { ImageMissingPlaceholder } from '@vs-shop/ui/imageMissingPlaceholder';

import { getDefaultCurrency } from '../../../config/currency';

import { resolveImageUrl } from '@vs-shop/engine/image/resolveImageUrl';

import { intlFormatMoney } from '../../server/intl/intlFormat';

import type { SuggestHit } from '../../../effect/schema/openapi/backend';
import { ImageFilter } from '@vs-shop/engine/image/ImageFilter';

type Props = {
  hit: SuggestHit | undefined;
  heading: string;
  // Optional: live suggest closes the popover before navigating; the server-rendered
  // default panel omits it (native anchor navigation closes it on page change).
  onSelect?: () => void;
};

/**
 * The single most relevant product, in the column it has to itself.
 *
 * The same row as the grid's, in its featured appearance: the crop above the
 * copy rather than beside it, both a step larger. No body column — the name and
 * the price are the link's own children, because there is nothing to push a
 * price down against.
 */
export default function SuggestTopProduct({ hit, heading, onSelect }: Props) {
  if (hit === undefined) return null;

  const currency = getDefaultCurrency();

  const imageSrc =
    hit.imagePath !== null
      ? resolveImageUrl(hit.imagePath, ImageFilter.product_card_search_suggest_top)
      : null;

  return (
    <SearchComboboxGroup>
      <SearchComboboxGroupLabel>{heading}</SearchComboboxGroupLabel>
      <SearchComboboxItem
        value={hit}
        render={
          <SearchSuggestProductLink
            variant={SearchSuggestVariant.FEATURE}
            href={hit.routerUrl}
            onClick={onSelect}
          />
        }
      >
        {imageSrc !== null ? (
          <SearchSuggestProductImage
            variant={SearchSuggestVariant.FEATURE}
            src={imageSrc}
            alt={hit.label}
            width={215}
            height={161}
          />
        ) : (
          <SearchSuggestProductImagePlaceholder variant={SearchSuggestVariant.FEATURE}>
            <ImageMissingPlaceholder />
          </SearchSuggestProductImagePlaceholder>
        )}
        <SearchSuggestProductLabel variant={SearchSuggestVariant.FEATURE}>
          {hit.label}
        </SearchSuggestProductLabel>
        {typeof hit.price === 'number' ? (
          <SearchSuggestProductPrice variant={SearchSuggestVariant.FEATURE}>
            {intlFormatMoney({ value: { amount: hit.price, currency } })}
          </SearchSuggestProductPrice>
        ) : null}
      </SearchComboboxItem>
    </SearchComboboxGroup>
  );
}
